import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { 
  ClipboardList, X, Copy, Check, Trash2, Eye, 
  Clock, CheckCircle, XCircle, Loader2, UserPlus, Search
} from 'lucide-react'
import { api } from '../lib/api'

// Sortable column header — matches PatientsPage style
function SortTh({ field, label, sort, onSort, align = 'left' }: { field: string; label: string; sort: string; onSort: (f: string) => void; align?: 'left' | 'right' }) {
  const [activeField, dir] = sort.split(':')
  const active = activeField === field
  const ChevronUp = (props: any) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>
  const ChevronDown = (props: any) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
  return (
    <th className={`font-medium px-4 py-2.5 select-none cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 ${align === 'right' ? 'text-right' : 'text-left'}`} onClick={() => onSort(field)}>
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''} ${active ? 'text-brand-600 dark:text-brand-300' : ''}`}>
        {label}
        {active && (dir === 'desc' ? <ChevronDown style={{ width: 12, height: 12 }} /> : <ChevronUp style={{ width: 12, height: 12 }} />)}
      </span>
    </th>
  )
}

interface IntakeSession {
  id: string
  patient_id: string | null
  patient_name: string | null
  patient_first_name: string | null
  patient_last_name: string | null
  visit_reason: string | null
  status: string
  channel: string
  completion_pct: number
  created_at: string
  updated_at: string
  expires_at: string
}

interface IntakeSessionDetail {
  id: string
  doctor_id: string
  patient_id: string | null
  patient_name: string | null
  patient_first_name: string | null
  patient_last_name: string | null
  encounter_id: string | null
  specialty: string
  visit_reason: string | null
  channel: string
  status: string
  messages: Array<{ role: string; content: string; timestamp: string }>
  extracted_data: any
  summary: string | null
  completion_pct: number
  duration_seconds: number | null
  created_at: string
  updated_at: string
}

export function IntakeManagementPage() {
  const [sessions, setSessions] = useState<IntakeSession[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState(() => localStorage.getItem('scribey_intake_sort') || 'created_at:desc')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedSession, setSelectedSession] = useState<IntakeSessionDetail | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    loadSessions()
  }, [])

  // Deep-link: open a specific session's detail when navigated with ?session=<id>
  // (e.g. from a patient's "View intake form" card). Clears the param after opening.
  useEffect(() => {
    const sid = searchParams.get('session')
    if (sid) {
      viewSession(sid)
      searchParams.delete('session')
      setSearchParams(searchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const loadSessions = async () => {
    try {
      setLoading(true)
      const data = await api.listIntakeSessions()
      setSessions(data)
    } catch (err) {
      console.error('Failed to load intake sessions:', err)
    } finally {
      setLoading(false)
    }
  }

  const viewSession = async (sessionId: string) => {
    try {
      const data = await api.getIntakeSession(sessionId)
      setSelectedSession(data)
    } catch (err) {
      console.error('Failed to load session details:', err)
    }
  }

  const deleteSession = async (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation()
    if (!confirm('Are you sure you want to delete this intake session?')) return

    try {
      await api.deleteIntakeSession(sessionId)
      setSessions(sessions.filter(s => s.id !== sessionId))
      if (selectedSession?.id === sessionId) {
        setSelectedSession(null)
      }
    } catch (err) {
      console.error('Failed to delete session:', err)
      alert('Failed to delete session')
    }
  }

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { label: string; color: string; icon: any }> = {
      pending: { label: 'Pending', color: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300', icon: Clock },
      in_progress: { label: 'In Progress', color: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300', icon: Loader2 },
      completed: { label: 'Completed', color: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300', icon: CheckCircle },
      expired: { label: 'Expired', color: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300', icon: XCircle },
      cancelled: { label: 'Cancelled', color: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400', icon: XCircle },
    }
    const badge = badges[status] || badges.pending
    const Icon = badge.icon
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    )
  }

  useEffect(() => {
    try { localStorage.setItem('scribey_intake_sort', sort) } catch {}
  }, [sort])

  function toggleSort(field: string) {
    const [f, d] = sort.split(':')
    setSort(f === field && d !== 'desc' ? `${field}:desc` : field)
  }

  const visibleSessions = sessions
    .filter(sn => {
      if (!query) return true
      const q = query.toLowerCase()
      const lastName = (sn.patient_last_name || sn.patient_name || '').toLowerCase()
      const firstName = (sn.patient_first_name || '').toLowerCase()
      return lastName.includes(q) || firstName.includes(q) || (sn.visit_reason || '').toLowerCase().includes(q)
    })
    .sort((a, b) => {
      const [field, dir] = sort.split(':')
      const mul = dir === 'desc' ? -1 : 1
      if (field === 'patient_last_name') {
        const lastCmp = mul * (a.patient_last_name || a.patient_name || '').localeCompare(b.patient_last_name || b.patient_name || '')
        if (lastCmp !== 0) return lastCmp
        return mul * (a.patient_first_name || '').localeCompare(b.patient_first_name || '')
      }
      if (field === 'patient_first_name') {
        return mul * (a.patient_first_name || '').localeCompare(b.patient_first_name || '')
      }
      if (field === 'status') {
        const order: Record<string, number> = { in_progress: 0, pending: 1, completed: 2, expired: 3, cancelled: 4 }
        return mul * ((order[a.status] ?? 9) - (order[b.status] ?? 9))
      }
      if (field === 'completion_pct') {
        return mul * (a.completion_pct - b.completion_pct)
      }
      if (field === 'expires_at') {
        return mul * (new Date(a.expires_at).getTime() - new Date(b.expires_at).getTime())
      }
      // created_at default
      return mul * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    })

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Patient Intake</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              Create and manage pre-visit intake sessions
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap self-start sm:self-center"
          >
            + New Intake
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by patient or visit reason..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-4 py-3 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>

      {/* Sessions Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-brand-500" />
        </div>
      ) : visibleSessions.length === 0 ? (
        <div className="bg-gray-100 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-12 text-center">
          <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{sessions.length === 0 ? 'No intake sessions yet' : 'No matching sessions'}</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {sessions.length === 0 ? 'Select a patient and send them an intake link before their visit' : 'Try a different search or sort'}
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
          >
            + New Intake
          </button>
        </div>
      ) : (
        <>
          {/* Desktop table — dense, scales to hundreds of intakes */}
          <div className="hidden md:block bg-gray-100 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-200/60 dark:bg-gray-900/60 text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide border-b border-gray-200 dark:border-gray-800">
                <tr>
                  <SortTh field="patient_last_name" label="Last name" sort={sort} onSort={toggleSort} />
                  <SortTh field="patient_first_name" label="First name" sort={sort} onSort={toggleSort} />
                  <th className="text-left font-medium px-4 py-2.5">Visit reason</th>
                  <SortTh field="status" label="Status" sort={sort} onSort={toggleSort} />
                  <SortTh field="completion_pct" label="Progress" sort={sort} onSort={toggleSort} />
                  <SortTh field="created_at" label="Created" sort={sort} onSort={toggleSort} />
                  <SortTh field="expires_at" label="Expires" sort={sort} onSort={toggleSort} />
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {visibleSessions.map((session) => (
                  <tr
                    key={session.id}
                    onClick={() => viewSession(session.id)}
                    className="hover:bg-gray-200/60 dark:hover:bg-gray-800/50 cursor-pointer group"
                  >
                    <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {session.patient_last_name || (session.patient_name?.split(' ').slice(-1)[0]) || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {session.patient_first_name || (session.patient_name?.split(' ')[0]) || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400 max-w-[16rem] truncate">
                      {session.visit_reason || '—'}
                    </td>
                    <td className="px-4 py-2.5">{getStatusBadge(session.status)}</td>
                    <td className="px-4 py-2.5">
                      {session.completion_pct > 0 ? (
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full bg-brand-500" style={{ width: `${session.completion_pct}%` }} />
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{Math.round(session.completion_pct)}%</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">{new Date(session.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">{new Date(session.expires_at).toLocaleDateString()}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => { e.stopPropagation(); viewSession(session.id) }}
                          className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-brand-500 dark:hover:text-brand-400 rounded"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => deleteSession(e, session.id)}
                          className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards — compact */}
          <div className="md:hidden space-y-2">
            {visibleSessions.map((session) => (
              <div
                key={session.id}
                onClick={() => viewSession(session.id)}
                className="bg-gray-100 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-3 active:bg-gray-200 dark:active:bg-gray-800 cursor-pointer"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">{session.patient_last_name || ''}{session.patient_last_name ? ', ' : ''}{session.patient_first_name || session.patient_name || 'Unknown Patient'}</h3>
                      {getStatusBadge(session.status)}
                    </div>
                    {session.visit_reason && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{session.visit_reason}</p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                      <span>Created {new Date(session.created_at).toLocaleDateString()}</span>
                      {session.completion_pct > 0 && (
                        <span className="text-brand-600 dark:text-brand-400 font-medium">{Math.round(session.completion_pct)}%</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => deleteSession(e, session.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 shrink-0"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {session.completion_pct > 0 && session.completion_pct < 100 && (
                  <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mt-2">
                    <div className="h-full bg-brand-500 transition-all" style={{ width: `${session.completion_pct}%` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateIntakeModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            loadSessions()
            setShowCreateModal(false)
          }}
        />
      )}

      {/* View Session Modal */}
      {selectedSession && (
        <ViewSessionModal
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
          onUpdated={loadSessions}
        />
      )}
    </div>
  )
}


// =============================================================================
// Create Intake Modal — Patient is REQUIRED
// =============================================================================

function CreateIntakeModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [patients, setPatients] = useState<any[]>([])
  const [loadingPatients, setLoadingPatients] = useState(true)
  const [creating, setCreating] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showNewPatient, setShowNewPatient] = useState(false)
  const [newPatient, setNewPatient] = useState({ last_name: '', first_name: '', dob: '' })
  const [creatingPatient, setCreatingPatient] = useState(false)

  const [formData, setFormData] = useState({
    patient_id: '',
    visit_reason: '',
    pin: '',
    expires_hours: 72,
  })
  const [createdSession, setCreatedSession] = useState<any>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    loadPatients()
  }, [])

  const loadPatients = async () => {
    try {
      const data = await api.listPatients(undefined, 200)
      setPatients(data.patients || [])
    } catch (err) {
      console.error('Failed to load patients:', err)
    } finally {
      setLoadingPatients(false)
    }
  }

  const filteredPatients = patients.filter(p => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    const name = `${p.last_name || ''} ${p.first_name || ''} ${p.name || ''}`.toLowerCase()
    return name.includes(q)
  })

  const formatPatientDisplay = (p: any) => {
    const last = p.last_name || ''
    const first = p.first_name || ''
    const name = last && first ? `${last}, ${first}` : p.name || 'Unknown'
    const dob = p.dob ? ` · ${new Date(p.dob).toLocaleDateString()}` : ''
    return `${name}${dob}`
  }

  const handleCreatePatient = async () => {
    if (!newPatient.last_name.trim() || !newPatient.first_name.trim()) {
      alert('First and last name are required')
      return
    }
    try {
      setCreatingPatient(true)
      const created = await api.createPatient({
        first_name: newPatient.first_name.trim(),
        last_name: newPatient.last_name.trim(),
        dob: newPatient.dob || undefined,
      })
      setPatients([created, ...patients])
      setFormData({ ...formData, patient_id: created.id })
      setShowNewPatient(false)
      setNewPatient({ last_name: '', first_name: '', dob: '' })
    } catch (err: any) {
      alert(err.message || 'Failed to create patient')
    } finally {
      setCreatingPatient(false)
    }
  }

  const createSession = async () => {
    if (!formData.patient_id) {
      alert('Please select a patient')
      return
    }
    try {
      setCreating(true)
      const payload: any = {
        patient_id: formData.patient_id,
        visit_reason: formData.visit_reason || undefined,
        expires_hours: formData.expires_hours,
      }
      if (formData.pin) payload.pin = formData.pin

      const session = await api.createIntakeSession(payload)
      setCreatedSession(session)
    } catch (err: any) {
      alert(err.message || 'Failed to create intake session')
      setCreating(false)
    }
  }

  const copyLink = () => {
    const url = `${window.location.origin}/intake/${createdSession.access_token}`
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // --- Success Screen ---
  if (createdSession) {
    const url = `${window.location.origin}/intake/${createdSession.access_token}`
    const selectedPatient = patients.find(p => p.id === formData.patient_id)
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Intake Link Created</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
              <X className="w-5 h-5" />
            </button>
          </div>

          {selectedPatient && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              For <span className="font-medium text-gray-900 dark:text-gray-100">{formatPatientDisplay(selectedPatient)}</span>
            </p>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Patient Access Link
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={url}
                  readOnly
                  className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100"
                />
                <button
                  onClick={copyLink}
                  className="px-3 py-2 bg-brand-600 hover:bg-brand-500 text-white rounded-lg transition-colors"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {createdSession.pin && (
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-4">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-1">
                  PIN for Patient
                </p>
                <p className="text-2xl font-mono font-bold text-amber-900 dark:text-amber-100 tracking-wider">
                  {createdSession.pin}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                  Give this PIN to the patient along with the link.
                </p>
              </div>
            )}

            <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-4">
              <p className="text-sm text-blue-900 dark:text-blue-200">
                <strong>Expires:</strong> {new Date(createdSession.expires_at).toLocaleString()}
              </p>
            </div>

            <button
              onClick={() => { onCreated(); onClose() }}
              className="w-full py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-lg font-medium transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    )
  }

  // --- Create Form ---
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">New Intake</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Patient Selection — REQUIRED */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Patient <span className="text-red-500">*</span>
            </label>

            {loadingPatients ? (
              <div className="text-sm text-gray-500 py-2">Loading patients...</div>
            ) : (
              <>
                {/* Search */}
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search patients..."
                    className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100"
                  />
                </div>

                {/* Patient list */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-48 overflow-y-auto">
                  {filteredPatients.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 p-3 text-center">
                      No patients found
                    </p>
                  ) : (
                    filteredPatients.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setFormData({ ...formData, patient_id: p.id })}
                        className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 dark:border-gray-700 last:border-0 transition-colors ${
                          formData.patient_id === p.id
                            ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 font-medium'
                            : 'text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {formatPatientDisplay(p)}
                      </button>
                    ))
                  )}
                </div>

                {/* New Patient inline */}
                {!showNewPatient ? (
                  <button
                    onClick={() => setShowNewPatient(true)}
                    className="flex items-center gap-1.5 mt-2 text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    New patient
                  </button>
                ) : (
                  <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={newPatient.last_name}
                        onChange={(e) => setNewPatient({ ...newPatient, last_name: e.target.value })}
                        placeholder="Last name"
                        className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-sm text-gray-900 dark:text-gray-100"
                      />
                      <input
                        type="text"
                        value={newPatient.first_name}
                        onChange={(e) => setNewPatient({ ...newPatient, first_name: e.target.value })}
                        placeholder="First name"
                        className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-sm text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <input
                      type="date"
                      value={newPatient.dob}
                      onChange={(e) => setNewPatient({ ...newPatient, dob: e.target.value })}
                      placeholder="DOB"
                      className="w-full px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-sm text-gray-900 dark:text-gray-100"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleCreatePatient}
                        disabled={creatingPatient}
                        className="px-3 py-1.5 bg-brand-600 hover:bg-brand-500 text-white rounded text-sm font-medium disabled:opacity-50"
                      >
                        {creatingPatient ? 'Creating...' : 'Add Patient'}
                      </button>
                      <button
                        onClick={() => setShowNewPatient(false)}
                        className="px-3 py-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Visit Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Visit Reason
            </label>
            <input
              type="text"
              value={formData.visit_reason}
              onChange={(e) => setFormData({ ...formData, visit_reason: e.target.value })}
              placeholder="e.g., Follow-up knee pain, New consult shoulder"
              className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {/* PIN */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              PIN <span className="text-xs text-gray-500">(auto-generated if blank)</span>
            </label>
            <input
              type="password"
              value={formData.pin}
              onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
              placeholder="4-6 digits"
              maxLength={6}
              className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {/* Expiration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Link Expiration
            </label>
            <select
              value={formData.expires_hours}
              onChange={(e) => setFormData({ ...formData, expires_hours: parseInt(e.target.value) })}
              className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value={24}>24 hours</option>
              <option value={48}>48 hours</option>
              <option value={72}>3 days</option>
              <option value={168}>1 week</option>
            </select>
          </div>

          {/* Submit */}
          <button
            onClick={createSession}
            disabled={creating || !formData.patient_id}
            className="w-full py-2.5 bg-brand-600 hover:bg-brand-500 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
          >
            {creating ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </span>
            ) : (
              'Create Intake Link'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}


// =============================================================================
// View Session Modal — Formatted clinical summary, no raw JSON
// =============================================================================

function ExtractedDataView({ data }: { data: any }) {
  if (!data || Object.keys(data).length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400 italic">No data extracted yet.</p>
  }

  const renderValue = (val: any): string => {
    if (val === null || val === undefined) return '—'
    if (Array.isArray(val)) return val.length === 0 ? '—' : val.join(', ')
    if (typeof val === 'string') return val || '—'
    return String(val)
  }

  const renderObject = (obj: any): string => {
    if (!obj || typeof obj !== 'object') return renderValue(obj)
    const parts = Object.entries(obj)
      .filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
      .map(([k, v]) => `${formatLabel(k)}: ${renderValue(v)}`)
    return parts.join('; ') || '—'
  }

  const formatLabel = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .replace(/Hpi/g, 'HPI')
      .replace(/Pmh/g, 'PMH')
      .replace(/Psh/g, 'PSH')
      .replace(/Adls/g, 'ADLs')
      .replace(/Ros/g, 'ROS')
      .replace(/Heent/g, 'HEENT')
      .replace(/Gi\b/g, 'GI')
      .replace(/Gu\b/g, 'GU')
      .replace(/Heme Lymph/g, 'Heme/Lymph')
  }

  // Sections to display in clinical order
  const sections: Array<{ key: string; label: string; type: 'text' | 'list' | 'object' | 'nested' }> = [
    { key: 'chief_complaint', label: 'Chief Complaint', type: 'text' },
    { key: 'hpi', label: 'HPI', type: 'object' },
    { key: 'past_medical_history', label: 'Past Medical History', type: 'list' },
    { key: 'past_surgical_history', label: 'Past Surgical History', type: 'list' },
    { key: 'medications', label: 'Medications', type: 'list' },
    { key: 'allergies', label: 'Allergies', type: 'list' },
    { key: 'family_history', label: 'Family History', type: 'list' },
    { key: 'social_history', label: 'Social History', type: 'object' },
    { key: 'review_of_systems', label: 'Review of Systems', type: 'nested' },
    { key: 'functional_status', label: 'Functional Status', type: 'object' },
    { key: 'current_treatments', label: 'Current Treatments', type: 'list' },
    { key: 'goals_of_visit', label: 'Goals of Visit', type: 'text' },
  ]

  return (
    <div className="space-y-3">
      {sections.map(({ key, label, type }) => {
        const val = data[key]
        if (val === null || val === undefined) return null
        if (Array.isArray(val) && val.length === 0) return null
        if (typeof val === 'object' && !Array.isArray(val)) {
          const hasContent = Object.values(val).some(
            (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && (v as any[]).length === 0)
          )
          if (!hasContent) return null
        }

        return (
          <div key={key}>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              {label}
            </h4>
            {type === 'text' && (
              <p className="text-sm text-gray-900 dark:text-gray-100">{renderValue(val)}</p>
            )}
            {type === 'list' && (
              <ul className="text-sm text-gray-900 dark:text-gray-100 space-y-0.5">
                {(Array.isArray(val) ? val : [val]).map((item: any, i: number) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-gray-400 mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                    {typeof item === 'string' ? item : JSON.stringify(item)}
                  </li>
                ))}
              </ul>
            )}
            {type === 'object' && (
              <p className="text-sm text-gray-900 dark:text-gray-100">{renderObject(val)}</p>
            )}
            {type === 'nested' && typeof val === 'object' && (
              <div className="text-sm text-gray-900 dark:text-gray-100 space-y-1">
                {Object.entries(val)
                  .filter(([, v]) => Array.isArray(v) && (v as any[]).length > 0)
                  .map(([system, items]) => (
                    <div key={system}>
                      <span className="font-medium text-gray-700 dark:text-gray-300">{formatLabel(system)}:</span>{' '}
                      {(items as string[]).join(', ')}
                    </div>
                  ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function ViewSessionModal({ session, onClose, onUpdated }: { session: IntakeSessionDetail; onClose: () => void; onUpdated?: () => void }) {
  const [activeTab, setActiveTab] = useState<'data' | 'conversation'>('data')
  const [showAssignPatient, setShowAssignPatient] = useState(false)
  const [patients, setPatients] = useState<any[]>([])
  const [loadingPatients, setLoadingPatients] = useState(false)
  const [assignSearch, setAssignSearch] = useState('')
  const [assigning, setAssigning] = useState(false)
  const [patientName, setPatientName] = useState(session.patient_name)

  const hasData = session.extracted_data && Object.keys(session.extracted_data).length > 0
  const hasMessages = session.messages && session.messages.length > 0

  // If no data yet, show conversation
  const effectiveTab = activeTab === 'data' && !hasData && hasMessages ? 'conversation' : activeTab

  const loadPatients = async () => {
    setLoadingPatients(true)
    try {
      const data = await api.listPatients(undefined, 200)
      setPatients(data.patients || [])
    } catch (err) {
      console.error(err)
    } finally {
      setLoadingPatients(false)
    }
  }

  const assignPatient = async (patientId: string) => {
    setAssigning(true)
    try {
      await api.updateIntakeSession(session.id, { patient_id: patientId })
      const p = patients.find(p => p.id === patientId)
      if (p) setPatientName(`${p.last_name}, ${p.first_name}`)
      setShowAssignPatient(false)
      onUpdated?.()
    } catch (err: any) {
      alert(err.message || 'Failed to assign patient')
    } finally {
      setAssigning(false)
    }
  }

  const filteredPatients = patients.filter(p => {
    if (!assignSearch) return true
    const q = assignSearch.toLowerCase()
    return `${p.last_name || ''} ${p.first_name || ''} ${p.name || ''}`.toLowerCase().includes(q)
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full my-8 flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {patientName || 'Unassigned'}
              </h2>
              <button
                onClick={() => { setShowAssignPatient(!showAssignPatient); if (!patients.length) loadPatients() }}
                className="text-xs text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
              >
                {patientName ? 'change' : 'assign patient'}
              </button>
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
              <span>{Math.round(session.completion_pct)}% complete</span>
              {session.duration_seconds && (
                <span>{Math.round(session.duration_seconds / 60)} min</span>
              )}
              <span>{new Date(session.created_at).toLocaleDateString()}</span>
            </div>
            {/* Inline patient assignment */}
            {showAssignPatient && (
              <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 w-72">
                <div className="relative mb-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="text" value={assignSearch} onChange={(e) => setAssignSearch(e.target.value)}
                    placeholder="Search patients..."
                    className="w-full pl-7 pr-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
                    autoFocus
                  />
                </div>
                <div className="max-h-32 overflow-y-auto">
                  {loadingPatients ? (
                    <p className="text-xs text-gray-500 p-2 text-center">Loading...</p>
                  ) : filteredPatients.length === 0 ? (
                    <p className="text-xs text-gray-500 p-2 text-center">No patients found</p>
                  ) : filteredPatients.map(p => (
                    <button
                      key={p.id}
                      onClick={() => assignPatient(p.id)}
                      disabled={assigning}
                      className="w-full text-left px-2 py-1.5 text-xs text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      {p.last_name}, {p.first_name}{p.dob ? ` · ${new Date(p.dob).toLocaleDateString()}` : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 self-start">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-6 flex gap-1">
          {[
            { id: 'data' as const, label: 'Patient Info', show: true },
            { id: 'conversation' as const, label: 'Conversation', show: hasMessages },
          ].filter(t => t.show).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                effectiveTab === tab.id
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Patient Info Tab — formatted extracted data */}
          {effectiveTab === 'data' && (
            hasData ? (
              <ExtractedDataView data={session.extracted_data} />
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">
                  {session.status === 'in_progress'
                    ? 'Data will appear as the patient answers questions.'
                    : session.status === 'completed'
                    ? 'No structured data was extracted.'
                    : 'Waiting for patient to start the intake.'}
                </p>
              </div>
            )
          )}

          {/* Conversation Tab */}
          {effectiveTab === 'conversation' && (
            <div className="space-y-3">
              {session.messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'patient' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-2 ${
                    msg.role === 'patient'
                      ? 'bg-brand-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <p className={`text-xs mt-1 ${msg.role === 'patient' ? 'text-white/60' : 'text-gray-400 dark:text-gray-500'}`}>
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
